import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  FileText, AlertTriangle, Users, Bell, Shield, TrendingUp, Building,
  Bug, Cloud, ChevronRight, Lock, CheckCircle, BarChart2, Layers,
  ClipboardCheck, Folder, Calendar, Loader2, AlertCircle, Eye, Radar
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import FrameworkReadinessWidget from '@/components/grc/FrameworkReadinessWidget';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExecutiveDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/dashboard/executive`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load executive dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Loading executive dashboard...</p>
      </div>
    </div>
  );

  if (!data) return null;

  const { grc, vendors, litesabre, amidala } = data;

  const riskDistribution = [
    { name: 'Critical', value: grc.risk_distribution?.critical || 0, color: '#DC2626' },
    { name: 'High', value: grc.risk_distribution?.high || 0, color: '#EA580C' },
    { name: 'Medium', value: grc.risk_distribution?.medium || 0, color: '#D97706' },
    { name: 'Low', value: grc.risk_distribution?.low || 0, color: '#16A34A' },
  ];

  const totalRiskCount = riskDistribution.reduce((s, r) => s + r.value, 0);

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="executive-dashboard">
      <div className="mb-2">
        <h1 className="text-2xl font-bold tracking-tight">Executive Dashboard</h1>
        <p className="text-sm text-muted-foreground">Real-time cross-module overview of your security and compliance posture</p>
      </div>

      {/* Top-level KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          label="Compliance Score"
          value={`${grc.compliance_score}%`}
          icon={Shield}
          color="text-emerald-600"
          bg="bg-emerald-50"
          trend={grc.compliance_score >= 80 ? 'good' : grc.compliance_score >= 60 ? 'warning' : 'bad'}
        />
        <KPICard
          label="Open Risks"
          value={grc.total_risks}
          icon={AlertTriangle}
          color="text-amber-600"
          bg="bg-amber-50"
          sub={`${grc.critical_risks} critical`}
          trend={grc.critical_risks > 0 ? 'bad' : 'good'}
        />
        <KPICard
          label="Total Vendors"
          value={vendors.total_vendors}
          icon={Building}
          color="text-blue-600"
          bg="bg-blue-50"
          sub={`${vendors.high_risk_vendors} high-risk`}
          trend={vendors.high_risk_vendors > 0 ? 'warning' : 'good'}
        />
        <KPICard
          label="Active Alerts"
          value={grc.total_alerts}
          icon={Bell}
          color="text-red-600"
          bg="bg-red-50"
          trend={grc.total_alerts > 5 ? 'bad' : grc.total_alerts > 0 ? 'warning' : 'good'}
        />
      </div>

      {/* Module Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* GRC Core */}
        <ModuleSection
          title="GRC Core"
          icon={Shield}
          color="blue"
          navigate={navigate}
          link="/grc/frameworks"
          items={[
            { label: 'Active Policies', value: grc.total_policies, icon: FileText },
            { label: 'Frameworks', value: grc.total_frameworks, icon: Layers },
            { label: 'Controls', value: grc.total_controls, icon: ClipboardCheck },
            { label: 'Documents', value: grc.total_documents, icon: Folder },
            { label: 'Scheduled Audits', value: grc.total_audits, icon: Calendar },
            { label: 'Critical Risks', value: grc.critical_risks, icon: AlertCircle, highlight: grc.critical_risks > 0 },
          ]}
        />

        {/* Vendor Risk */}
        <ModuleSection
          title="Vendor Risk Management"
          icon={Building}
          color="indigo"
          navigate={navigate}
          link="/grc/vendor-assessment"
          items={[
            { label: 'Total Vendors', value: vendors.total_vendors, icon: Users },
            { label: 'High-Risk Vendors', value: vendors.high_risk_vendors, icon: AlertTriangle, highlight: vendors.high_risk_vendors > 0 },
            { label: 'Pending Assessments', value: vendors.pending_assessments, icon: ClipboardCheck },
            { label: 'Completed', value: vendors.completed_assessments, icon: CheckCircle },
            { label: 'Overdue', value: vendors.overdue_assessments, icon: AlertCircle, highlight: vendors.overdue_assessments > 0 },
          ]}
        />

        {/* LiteSabre */}
        <ModuleSection
          title="LiteSabre Scanner"
          icon={Bug}
          color="red"
          navigate={navigate}
          link="/litesabre/dashboard"
          items={[
            { label: 'Total Scans', value: litesabre.total_scans, icon: Eye },
            { label: 'Active Scans', value: litesabre.active_scans, icon: Radar },
            { label: 'Vulnerabilities', value: litesabre.total_vulnerabilities, icon: Bug },
            { label: 'Critical Findings', value: litesabre.critical_findings, icon: AlertCircle, highlight: litesabre.critical_findings > 0 },
            { label: 'High Findings', value: litesabre.high_findings, icon: AlertTriangle, highlight: litesabre.high_findings > 0 },
          ]}
        />

        {/* Amidala CSPM */}
        <ModuleSection
          title="Amidala CSPM"
          icon={Cloud}
          color="purple"
          navigate={navigate}
          link="/amidala/dashboard"
          items={[
            { label: 'Cloud Accounts', value: amidala.total_cloud_accounts, icon: Cloud },
            { label: 'CSPM Scans', value: amidala.cspm_scans, icon: Eye },
            { label: 'Misconfigurations', value: amidala.total_misconfigurations, icon: AlertTriangle, highlight: amidala.total_misconfigurations > 0 },
            { label: 'Security Score', value: amidala.cloud_security_score ? `${amidala.cloud_security_score}%` : 'N/A', icon: Shield },
          ]}
        />
      </div>

      {/* Framework Readiness — Evidence Collection */}
      <FrameworkReadinessWidget />

      {/* Risk Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Risk Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          {totalRiskCount === 0 ? (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <div className="text-center">
                <Shield className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm font-medium">No open risks</p>
                <p className="text-xs text-muted-foreground">Your risk register is clear</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-8">
              <div className="w-48 h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={riskDistribution.filter(r => r.value > 0)} cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={3} dataKey="value">
                      {riskDistribution.filter(r => r.value > 0).map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-3">
                {riskDistribution.map(r => (
                  <div key={r.name} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color }} />
                    <div>
                      <p className="text-xs text-muted-foreground">{r.name}</p>
                      <p className="text-lg font-bold">{r.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function KPICard({ label, value, icon: Icon, color, bg, sub, trend }) {
  const trendColors = { good: 'border-emerald-200', warning: 'border-amber-200', bad: 'border-red-200' };
  return (
    <Card className={`border-2 ${trendColors[trend] || 'border-transparent'}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
            <p className="text-3xl font-bold">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ModuleSection({ title, icon: Icon, color, items, navigate, link }) {
  const colorMap = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100', header: 'bg-blue-600' },
    indigo: { bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-100', header: 'bg-indigo-600' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100', header: 'bg-red-600' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-100', header: 'bg-purple-600' },
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <Card className={`border ${c.border} overflow-hidden`} data-testid={`module-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`${c.header} px-4 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-white" />
          <span className="text-sm font-semibold text-white">{title}</span>
        </div>
        <button onClick={() => navigate(link)} className="text-white/70 hover:text-white transition-colors flex items-center gap-1 text-xs">
          View <ChevronRight className="w-3 h-3" />
        </button>
      </div>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-100">
          {items.map((item, i) => {
            const ItemIcon = item.icon;
            return (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors">
                <div className="flex items-center gap-2.5">
                  <ItemIcon className={`w-4 h-4 ${item.highlight ? 'text-red-500' : 'text-muted-foreground'}`} />
                  <span className="text-sm text-muted-foreground">{item.label}</span>
                </div>
                <span className={`text-sm font-semibold ${item.highlight ? 'text-red-600' : ''}`}>{item.value}</span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default ExecutiveDashboard;
